import React, { useMemo } from 'react';
import {
    buildCustomMilestoneSchedule,
    buildMilestoneSchedule,
} from '../../utils/milestoneSchedule';

const StatPill = ({ label, value, sub, accent = 'teal' }) => {
    const accentMap = {
        teal: 'border-teal-200/80 bg-teal-50/80 text-teal-900 dark:border-emerald-500/20 dark:bg-emerald-500/[0.07] dark:text-emerald-300',
        zinc: 'border-zinc-200 bg-zinc-50/90 text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-zinc-200',
    };
    return (
        <div
            className={`rounded-xl border px-3 py-2.5 ${accentMap[accent] || accentMap.zinc}`}
        >
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-70">
                {label}
            </p>
            <p className="mt-0.5 text-sm font-bold tabular-nums leading-tight">{value}</p>
            {sub ? (
                <p className="mt-0.5 text-[10px] font-medium opacity-75">{sub}</p>
            ) : null}
        </div>
    );
};

const MilestoneSchedulePreview = ({
    totalAmount,
    milestoneCount,
    intervalSeconds,
    customSchedule = null,
    tokenSymbol = '',
    className = '',
}) => {
    const schedule = useMemo(() => {
        if (customSchedule) return customSchedule;
        return buildMilestoneSchedule({
            totalAmount,
            milestoneCount,
            intervalSeconds,
        });
    }, [customSchedule, totalAmount, milestoneCount, intervalSeconds]);

    if (!schedule) return null;

    const sym = tokenSymbol ? ` ${tokenSymbol}` : '';
    const segmentWidthPct = 100 / schedule.milestoneCount;
    const maxBarSegments = 16;
    const visibleSegments = Math.min(schedule.milestoneCount, maxBarSegments);
    const hiddenSegments = schedule.milestoneCount - visibleSegments;

    return (
        <div
            className={`overflow-hidden rounded-2xl border border-teal-200/80 bg-gradient-to-br from-teal-50/90 via-white to-zinc-50/80 dark:border-zinc-800 dark:from-zinc-950/70 dark:via-zinc-950/50 dark:to-zinc-950/80 ${className}`}
        >
            <div className="flex items-center justify-between gap-3 border-b border-teal-200/60 px-4 py-3 dark:border-zinc-800">
                <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-100 text-teal-800 dark:bg-emerald-500/15 dark:text-emerald-400">
                        <i className="fa-solid fa-chart-pie text-sm" aria-hidden />
                    </span>
                    <div>
                        <p className="text-xs font-bold text-zinc-900 dark:text-white">
                            Payment schedule preview
                        </p>
                        <p className="text-[11px] text-zinc-500 dark:text-zinc-500">
                            {schedule.customSchedule
                                ? 'Custom unlock times and amounts'
                                : 'Estimated unlocks from contract start (today)'}
                        </p>
                    </div>
                </div>
                <span className="rounded-full bg-teal-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-teal-800 dark:bg-emerald-500/15 dark:text-emerald-300">
                    {schedule.milestoneCount} milestones
                </span>
            </div>

            <div className="space-y-4 p-4">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <StatPill
                        label="Total locked"
                        value={`${schedule.totalFormatted}${sym}`}
                        sub="Held in escrow"
                    />
                    {schedule.customSchedule ? (
                        <>
                            <StatPill
                                label="Schedule type"
                                value="Custom"
                                sub="Per-milestone amounts"
                                accent="zinc"
                            />
                            <StatPill
                                label="Final unlock"
                                value={schedule.endDateLabel || '—'}
                                sub="Last milestone"
                                accent="zinc"
                            />
                        </>
                    ) : (
                        <>
                            <StatPill
                                label="Per milestone"
                                value={`${schedule.perMilestoneFormatted}${sym}`}
                                sub={
                                    schedule.hasRemainder
                                        ? `+${schedule.remainderFormatted} on final`
                                        : 'Equal split'
                                }
                            />
                            <StatPill
                                label="Each period"
                                value={schedule.intervalLabel}
                                sub="Time between unlocks"
                                accent="zinc"
                            />
                            <StatPill
                                label="Full schedule"
                                value={schedule.totalDurationLabel}
                                sub={`Ends ~${schedule.endDateLabel}`}
                                accent="zinc"
                            />
                        </>
                    )}
                </div>

                <div>
                    <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-500">
                        <span>Amount distribution</span>
                        <span className="tabular-nums">
                            {schedule.totalFormatted}
                            {sym}
                        </span>
                    </div>
                    <div
                        className="flex h-3 overflow-hidden rounded-full bg-zinc-200/80 dark:bg-zinc-800"
                        role="img"
                        aria-label={`${schedule.milestoneCount} equal milestone segments`}
                    >
                        {schedule.milestones.slice(0, visibleSegments).map((m, idx) => (
                            <div
                                key={m.index}
                                title={`${m.label}: ${m.amountFormatted}${sym}`}
                                className="h-full min-w-[2px] border-r border-white/30 bg-teal-600 last:border-r-0 dark:border-zinc-900/40 dark:bg-emerald-500"
                                style={{
                                    width: `${segmentWidthPct}%`,
                                    opacity:
                                        0.35 +
                                        (idx / Math.max(visibleSegments - 1, 1)) *
                                            0.55,
                                }}
                            />
                        ))}
                        {hiddenSegments > 0 ? (
                            <div
                                className="h-full bg-zinc-400/60 dark:bg-zinc-600"
                                style={{ width: `${segmentWidthPct * hiddenSegments}%` }}
                                title={`+${hiddenSegments} more milestones`}
                            />
                        ) : null}
                    </div>
                    <p className="mt-1.5 text-[10px] text-zinc-500 dark:text-zinc-500">
                        Receiver can claim each slice once the unlock time passes (on-chain).
                    </p>
                </div>

                <div className="max-h-[280px] overflow-y-auto rounded-xl border border-zinc-200/80 bg-white/70 dark:border-zinc-800 dark:bg-zinc-950/30">
                    <table className="w-full min-w-[320px] text-left text-xs">
                        <thead className="sticky top-0 z-[1] border-b border-zinc-200 bg-zinc-50/95 text-[10px] font-bold uppercase tracking-wide text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/95 dark:text-zinc-500">
                            <tr>
                                <th className="px-3 py-2">#</th>
                                <th className="px-3 py-2">Unlock</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="hidden px-3 py-2 text-right sm:table-cell">
                                    Paid out (cum.)
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/80">
                            {schedule.milestones.map((m) => (
                                <tr
                                    key={m.index}
                                    className="transition-colors hover:bg-teal-50/50 dark:hover:bg-emerald-500/[0.05]"
                                >
                                    <td className="px-3 py-2.5">
                                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-teal-100 text-[10px] font-bold text-teal-800 dark:bg-emerald-500/15 dark:text-emerald-400">
                                            {m.index}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <p className="font-semibold text-zinc-800 dark:text-zinc-200">
                                            {m.elapsedLabel}
                                        </p>
                                        <p className="mt-0.5 text-[10px] text-zinc-500 dark:text-zinc-500">
                                            ~{m.unlockDateLabel}
                                        </p>
                                    </td>
                                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-zinc-900 dark:text-white">
                                        {m.amountFormatted}
                                        <span className="font-normal text-zinc-500">{sym}</span>
                                    </td>
                                    <td className="hidden px-3 py-2.5 text-right tabular-nums text-teal-800 dark:text-emerald-400 sm:table-cell">
                                        {m.cumulativeFormatted}
                                        <span className="text-zinc-500">{sym}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="border-t border-zinc-200 bg-zinc-50/90 dark:border-zinc-800 dark:bg-zinc-900/50">
                            <tr>
                                <td
                                    colSpan={2}
                                    className="px-3 py-2 text-[11px] font-semibold text-zinc-600 dark:text-zinc-400"
                                >
                                    After all milestones
                                </td>
                                <td className="px-3 py-2 text-right text-[11px] font-bold tabular-nums text-teal-800 dark:text-emerald-300">
                                    {schedule.totalFormatted}
                                    {sym}
                                </td>
                                <td className="hidden px-3 py-2 sm:table-cell" />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default MilestoneSchedulePreview;
